/**
 * Typed wrappers for the citizen self-service delivery rescheduling endpoints
 * on service-mail. Sibling of `address-change.ts` (F-076) — the rescheduling
 * feature shifts the delivery date without changing the address.
 *
 * Endpoints:
 *   POST /v1/mail-items/:trackingNumber/reschedule-requests
 *   GET  /v1/mail-items/:trackingNumber/reschedule-requests
 *
 * RBAC: requires Mail/requestReschedule-own (POST) and Mail/listReschedule-own (GET),
 * both seeded to the `customer` role by 20260603_citizen_reschedule_pickup_perms.sql.
 *
 * Identity: backend resolves the citizen from the JWT `customerId` claim and
 * compares it against the mail item's sender/receiver/bookedByCustomerId.
 */
import {
  requestJson,
  type RequestJsonOptions,
} from '../api-client';
import { getCustomerToken } from '../auth/tokenStore';
import { env } from '../env';

export interface CreateRescheduleRequest {
  /** YYYY-MM-DD, must be a future calendar day. */
  readonly requestedDeliveryDate: string;
  readonly reason?: string;
}

export interface RescheduleResponse {
  readonly id: string;
  readonly mailItemId: string;
  readonly customerId: string;
  readonly requestedDeliveryDate: string; // YYYY-MM-DD
  readonly reason: string | null;
  readonly feeMinor: number;
  /** "0.00" | "200.00" — already pre-formatted as a 2dp NGN string. */
  readonly feeNgn: string;
  readonly status: 'REQUESTED' | 'APPLIED' | 'REJECTED' | string;
  readonly chargedTransactionId: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

type CallOpts = Pick<RequestJsonOptions, 'fetchImpl' | 'signal'> & {
  readonly bearerToken?: string;
};

function bearerOf(opts: CallOpts): string | undefined {
  return opts.bearerToken ?? getCustomerToken() ?? undefined;
}

/**
 * POST /v1/mail-items/:trackingNumber/reschedule-requests
 * Submits a delivery-date reschedule for an in-flight parcel.
 *
 * The first reschedule per parcel is free; subsequent ones cost ₦200 (auto-
 * debited from the citizen's wallet).
 */
export async function requestReschedule(
  trackingNumber: string,
  body: CreateRescheduleRequest,
  options: CallOpts = {},
): Promise<RescheduleResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<RescheduleResponse>({
    method: 'POST',
    baseUrl: env.serviceMailBaseUrl,
    path: `/v1/mail-items/${encodeURIComponent(trackingNumber)}/reschedule-requests`,
    body,
    bearerToken: bearerOf(options),
    ...rest,
  });
}

/**
 * GET /v1/mail-items/:trackingNumber/reschedule-requests
 * Returns the citizen's reschedules for a parcel, newest-first.
 */
export async function listRescheduleRequests(
  trackingNumber: string,
  options: CallOpts = {},
): Promise<readonly RescheduleResponse[]> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<readonly RescheduleResponse[]>({
    method: 'GET',
    baseUrl: env.serviceMailBaseUrl,
    path: `/v1/mail-items/${encodeURIComponent(trackingNumber)}/reschedule-requests`,
    bearerToken: bearerOf(options),
    ...rest,
  });
}
